import React from 'react';
import { useSelector } from 'react-redux';
import { BrandColor } from '@styleseat/brand';
import { APP } from '../../../config';
import {
  useIsMobile,
  useFeatureFlag,
  useInTest,
} from '../../../hooks';
import analytics from '../../../modules/analytics';
import { SEARCH_TAILORED_UX_TEST_NAME } from '../../../modules/consumer/search/Constants';
import { BOOKING_ROUTE } from '../../../route-names';
import { RootState } from '../../../store/models';
import { APP_INSTALL_BANNER_ENABLED_FLAG_NAME } from './constants';

const getSmartBanner = () => (window as any).smartbanner;

const trackAction = event => {
  analytics.track(event, {
    platform: getSmartBanner()?.platform,
  });
};

const EXCLUDED_ROUTES = [BOOKING_ROUTE];

/**
 * The AppInstallBanner component wraps a third party smartbanner.js library
 * https://github.com/ain/smartbanner.js
 * The third party library reads meta tag configuration in the index.html to determine
 * when and which platform banner to show.
 * This component helps manage other eligibility and visual requirements within the app.
 */
export const AppInstallBanner: React.FC = () => {
  // Eligibility as determined by smartbanner.js
  const [isEligible, setEligible] = React.useState<boolean>(false);
  // Visibility as determined by this component
  const [isVisible, setVisible] = React.useState<boolean>(false);
  const isMobile = useIsMobile();
  const routeName = useSelector<RootState, string>(
    state => state.route.name,
  ) || '';

  const handleViewEvent = () => {
    setEligible(true);
    // No analytics event here, since we have more logic
    // later to determine if the banner is actually visible
  };

  const handleDismissEvent = () => {
    trackAction('client_appinstallbanner_dismissed');
    setEligible(false);
  };

  const handleClickAppStoreEvent = () => {
    getSmartBanner()?.exit?.(); // dismiss the banner when user goes to app store
    trackAction('client_appinstallbanner_view_clicked');
  };

  React.useEffect(() => {
    document.addEventListener('smartbanner.view', handleViewEvent);
    document.addEventListener('smartbanner.exit', handleDismissEvent);
    document.addEventListener('smartbanner.clickout', handleClickAppStoreEvent);
    getSmartBanner()?.publish?.();

    return () => {
      const banner = document.querySelector('.js_smartbanner');
      if (banner) {
        document.querySelector('body')?.removeChild(banner);
        document.removeEventListener('smartbanner.view', handleViewEvent);
        document.removeEventListener('smartbanner.exit', handleDismissEvent);
        document.removeEventListener('smartbanner.clickout', handleClickAppStoreEvent);
      }
    };
  }, []);

  React.useEffect(() => {
    setVisible(isMobile && !EXCLUDED_ROUTES.includes(routeName));
  }, [
    isMobile,
    routeName,
  ]);

  if (!isEligible) {
    return null;
  }

  return (
    <style>
      {/*
      Hack the app styling to make room for the banner,
      even with absolute positioned elements
      */}
      {isEligible && isVisible ? `
        [data-app-mount], .react-responsive-layout {
          position: absolute;
          top: 84px !important;
          left: 0;
          right: 0;
          bottom: 0;
        }
        [data-app-mount] .react-responsive-layout {
          top: 0px !important;
        }
        .modal-component-wrapper, .modal-component {
          top: 84px !important;
          top: calc(84px + constant(safe-area-inset-top)) !important;
          top: calc(84px + env(safe-area-inset-top)) !important;
        }
        .popover-component.open .popover-content,
        .query-filter-component.focused .react-autosuggest__container {
          top: 84px !important;
        }
        .smartbanner__button {
          color: ${BrandColor.Punch} !important;
        }
      ` : ''}
      {isEligible && !isVisible ? `
        .smartbanner {
          display: none;
          visibility: hidden;
        }
      ` : ''}
    </style>
  );
};

// Only attempt to render the app install banner when high level conditions are met
// This avoids complicated logic in the banner hooks
const AppInstallBannerWrapper = () => {
  const {
    enabled,
    loading,
  } = useFeatureFlag(APP_INSTALL_BANNER_ENABLED_FLAG_NAME);

  const isInTailoredUXTest = useInTest(SEARCH_TAILORED_UX_TEST_NAME);
  const routeName = useSelector<RootState, string>(state => state.route.name || '');

  if (APP || loading || !enabled || (isInTailoredUXTest && routeName.includes('search'))) {
    return null;
  }

  return <AppInstallBanner />;
};

export default AppInstallBannerWrapper;
